/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Chat Context - Refactored
 * 
 * Main chat context using extracted managers for session, message, and execution handling.
 * Provides a clean, focused interface for chat functionality.
 */

import React, { createContext, useContext, useReducer, useEffect, ReactNode, useRef, useCallback } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { updateChatSession } from '@shared/lib/api/chat';

// Extracted modules
import { ChatState, ChatAction, ChatMessage, ChatContextType, initialState, AgentContextProvider } from './types';
import { chatReducer } from './reducer';
import { SessionManager } from './session-manager';
import { MessageManager } from './message-manager';
import { ExecutionManager } from './execution-manager';

const ChatContext = createContext<ChatContextType | undefined>(undefined);

/**
 * Minimal user info required by ChatProvider.
 * External applications can provide their own user object as long as it has these properties.
 */
export interface ChatUser {
  id: number | string;
  name?: string;
  email?: string;
}

/**
 * Context for providing user info to ChatProvider from external sources.
 * This allows external apps to inject their user without using SkillShop's AuthContext.
 */
const ChatUserContext = createContext<ChatUser | null>(null);

/**
 * Provider for external apps to inject their user info into ChatProvider.
 * 
 * Example:
 * ```tsx
 * <ChatUserProvider user={{ id: myUser.id, name: myUser.name }}>
 *   <ChatProvider>
 *     <AgentSidebar />
 *   </ChatProvider>
 * </ChatUserProvider>
 * ```
 */
export function ChatUserProvider({ user, children }: { user: ChatUser | null; children: ReactNode }) {
  return (
    <ChatUserContext.Provider value={user}>
      {children}
    </ChatUserContext.Provider>
  );
}

export interface ChatProviderProps {
  children: ReactNode;
  /**
   * Optional function that provides context variables for all agent executions.
   * Called before each agent request to get the current context state.
   * 
   * Example:
   * ```typescript
   * const contextProvider = () => ({
   *   activeProduct: product,
   *   repositoryUrl: repoUrl,
   *   userRole: 'admin'
   * });
   * ```
   */
  contextProvider?: AgentContextProvider;
  /**
   * User object for the chat session. Required for external applications.
   * Internal SkillShop apps can omit this and wrap with AuthenticatedChatProvider instead.
   * 
   * Example:
   * ```typescript
   * <ChatProvider user={{ id: currentUser.id, name: currentUser.name }}>
   *   <AgentSidebar />
   * </ChatProvider>
   * ```
   */
  user?: ChatUser | null;
  /**
   * When embedding the agent-runtime bundle in an app whose bundler resolves
   * `@tanstack/react-query` to a different physical package than the host app
   * (e.g. Next.js + Turbopack resolving from the skill-shop package path),
   * pass the same `QueryClient` instance you use with `QueryClientProvider`.
   * ChatProvider wraps the tree with `QueryClientProvider` from this bundle's
   * react-query copy so hooks inside the bundle see a consistent context.
   */
  queryClient?: QueryClient;
}

function ChatProviderWithContextClient({
  children,
  contextProvider,
  user: userProp,
}: Omit<ChatProviderProps, 'queryClient'>) {
  const queryClient = useQueryClient();
  return (
    <ChatProviderInner contextProvider={contextProvider} user={userProp} queryClient={queryClient}>
      {children}
    </ChatProviderInner>
  );
}

function ChatProviderInner({
  children,
  contextProvider,
  user: userProp,
  queryClient,
}: ChatProviderProps & { queryClient: QueryClient }) {
  const [state, dispatch] = useReducer(chatReducer, initialState);

  // Get user from props or ChatUserContext
  const contextUser = useContext(ChatUserContext);
  const user = userProp ?? contextUser;
  const isSwitchingSession = useRef(false);
  const sessionSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const switchingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const titleGenerationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Store context provider in a ref to avoid re-creating managers on every render
  const contextProviderRef = useRef(contextProvider);
  
  // Update ref when provider changes
  useEffect(() => {
    contextProviderRef.current = contextProvider;
  }, [contextProvider]);

  // Initialize managers
  const sessionManager = new SessionManager(dispatch, queryClient);
  const messageManager = new MessageManager(dispatch, queryClient, user);
  const executionManager = new ExecutionManager(dispatch, queryClient, sessionManager, messageManager, user, contextProviderRef);

  // Helper function to safely clear timeouts
  const clearTimeoutSafely = (timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // Try to restore user's last session on mount (but don't create new one)
  useEffect(() => {
    const restoreSession = async () => {
      const restoredSessionId = await sessionManager.tryRestoreSession();
      // Load messages for the restored session
      if (restoredSessionId) {
        await messageManager.loadMessages(restoredSessionId);
      }
    };
    restoreSession();
  }, []);

  // Check execution status when session changes or loading state changes
  useEffect(() => {
    if (state.dbSessionId) {
      // Check execution status when session changes or when loading stops
      // (execution might have started/stopped)
      executionManager.updateExecutionStatusFromServer(state.dbSessionId, state);
    } else {
      // No session = no execution
      if (state.isExecuting) {
        dispatch({ type: 'SET_EXECUTING', payload: false });
      }
    }
  }, [state.dbSessionId, state.isLoading]);

  // Save UI state to database when it changes (but not when switching sessions)
  useEffect(() => {
    const saveSessionState = async () => {
      // Don't save session state while switching sessions to avoid unnecessary updates
      if (!state.dbSessionId || isSwitchingSession.current) return;
      
      try {
        await updateChatSession(state.dbSessionId, {
          isExpanded: state.isExpanded,
          sidebarWidth: state.sidebarWidth,
          honchoWorkspaceId: state.workspaceId,
          honchoSessionId: state.honchoSessionId,
          honchoUserPeerId: state.peerId,
        });
      } catch (error) {
        console.error('Error saving session state to database:', error);
      }
    };
    
    // Clear any existing timeout to prevent multiple saves
    clearTimeoutSafely(sessionSaveTimeoutRef);
    
    // Debounce the save operation
    sessionSaveTimeoutRef.current = setTimeout(saveSessionState, 500);
    
    return () => {
      clearTimeoutSafely(sessionSaveTimeoutRef);
    };
  }, [state.isExpanded, state.sidebarWidth, state.workspaceId, state.honchoSessionId, state.peerId, state.dbSessionId]);

  // Comprehensive timeout cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup all timeouts on unmount to prevent memory leaks
      const timeoutRefs = [
        sessionSaveTimeoutRef,
        switchingTimeoutRef,
        titleGenerationTimeoutRef
      ];
      
      timeoutRefs.forEach(clearTimeoutSafely);
    };
  }, []);

  // Action functions
  const setExpanded = (expanded: boolean) => {
    dispatch({ type: 'SET_EXPANDED', payload: expanded });
  };

  const setSidebarWidth = (width: number) => {
    dispatch({ type: 'SET_SIDEBAR_WIDTH', payload: width });
  };

  const setSelectedAgent = (agentName: string) => {
    dispatch({ type: 'SET_SELECTED_AGENT', payload: agentName });
  };

  const setIsLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  // Load session with switching protection
  const loadSession = async (sessionId: number) => {
    await sessionManager.loadSession(sessionId, state, isSwitchingSession);
    
    // Load messages for this session
    await messageManager.loadMessages(sessionId);
    
    // Extended delay to ensure all useEffect debounces complete, even with rapid clicking
    clearTimeoutSafely(switchingTimeoutRef);
    switchingTimeoutRef.current = setTimeout(() => {
      isSwitchingSession.current = false;
      switchingTimeoutRef.current = null;
    }, 2000); // 2 second delay to handle rapid session switching
  };

  const terminateCurrentExecution = async (reason?: string): Promise<boolean> => {
    if (!state.currentExecutionId) {
      console.warn('No current execution to terminate');
      return false;
    }
    
    return executionManager.terminateCurrentExecution(
      state.currentExecutionId, 
      reason || 'User requested termination'
    );
  };

  // Helper to get current context
  const getAgentContext = useCallback(() => {
    return contextProviderRef.current?.() ?? {};
  }, []);

  const contextValue: ChatContextType = {
    state,
    dispatch,
    setExpanded,
    setSidebarWidth,
    setSelectedAgent,
    addMessage: (message: ChatMessage, sessionId?: number) => messageManager.addMessage(message, state, sessionId),
    loadMessages: messageManager.loadMessages.bind(messageManager),
    initializeHoncho: () => messageManager.initializeHoncho(state),
    resetChat: () => sessionManager.resetChat(state),
    loadSession,
    loadSessionByKey: sessionManager.loadSessionByKey.bind(sessionManager),
    setIsLoading,
    updateSessionTitle: messageManager.updateSessionTitle.bind(messageManager),
    updateMessageExecutionState: messageManager.updateMessageExecutionState.bind(messageManager),
    executeAgentWithStreaming: (agentName: string, message: string, fileAttachment?: File) => 
      executionManager.executeAgentWithStreaming(agentName, message, fileAttachment, state, setIsLoading, titleGenerationTimeoutRef),
    terminateCurrentExecution,
    startNewSession: () => sessionManager.startNewSession(state),
    createSessionWithContext: (title: string, context: Record<string, any>, metadata?: Record<string, any>) =>
      sessionManager.createSessionWithContext(title, context, metadata),
    getAgentContext,
  };

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  );
}

export function ChatProvider({ children, contextProvider, user: userProp, queryClient: queryClientProp }: ChatProviderProps) {
  if (queryClientProp !== undefined) {
    return (
      <QueryClientProvider client={queryClientProp}>
        <ChatProviderInner contextProvider={contextProvider} user={userProp} queryClient={queryClientProp}>
          {children}
        </ChatProviderInner>
      </QueryClientProvider>
    );
  }
  return (
    <ChatProviderWithContextClient contextProvider={contextProvider} user={userProp}>
      {children}
    </ChatProviderWithContextClient>
  );
}

export function useChatContext() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
}

// Re-export types for backward compatibility
export type { ChatMessage, SubAgentCall, ChatState, ChatContextType } from './types';
